import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Receipt, Calendar, Eye, Edit, Trash2, FileText,
  Banknote, Smartphone, Building2, CreditCard, Tag, TrendingDown,
  MoreVertical, Download
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { expensesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PAYMENT_MODE_CONFIG = {
  CASH: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  UPI: { icon: Smartphone, label: 'UPI', bg: 'bg-teal-100', text: 'text-teal-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-teal-100', text: 'text-teal-700' },
  BANK: { icon: Building2, label: 'Bank', bg: 'bg-blue-100', text: 'text-blue-700' },
  bank_transfer: { icon: Building2, label: 'Bank', bg: 'bg-blue-100', text: 'text-blue-700' },
  CARD: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  CHEQUE: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
};

const CATEGORY_COLORS = {
  'Rent': 'bg-violet-100 text-violet-700',
  'Electricity': 'bg-yellow-100 text-yellow-700',
  'Salaries & Advances': 'bg-blue-100 text-blue-700',
  'Raw Material': 'bg-emerald-100 text-emerald-700',
  'Transportation': 'bg-orange-100 text-orange-700',
  'Marketing': 'bg-pink-100 text-pink-700',
  'Office Supplies': 'bg-cyan-100 text-cyan-700',
  'Bank Fees': 'bg-red-100 text-red-700',
  'Insurance': 'bg-indigo-100 text-indigo-700',
  'Maintenance': 'bg-teal-100 text-teal-700',
  'Professional Fees': 'bg-purple-100 text-purple-700',
  'Fuel': 'bg-amber-100 text-amber-700',
  'Internet/Phone': 'bg-sky-100 text-sky-700',
  'Travel': 'bg-rose-100 text-rose-700',
  'Meals': 'bg-lime-100 text-lime-700',
  'Other': 'bg-slate-100 text-slate-700',
};

const ExpensesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState({ total: 0, this_month: 0, categories: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (bid) {
      fetchExpenses();
    }
  }, [bid, categoryFilter]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await expensesAPI.list(params);
      const data = res.data;
      if (data) {
        const expenseList = data.expenses || data || [];
        setExpenses(expenseList);

        // Use server stats if available, else compute locally
        if (data.stats) {
          const uniqueCategories = [...new Set(expenseList.map(e => e.category))];
          setCategories(uniqueCategories);
          setStats({
            total: data.stats.total_count || expenseList.length,
            this_month: expenseList.filter(e => {
              const d = new Date(e.date || e.expense_date);
              const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length,
            categories: uniqueCategories.length,
            total_amount: data.stats.total_amount || 0,
          });
        } else {
          const totalAmount = expenseList.reduce((sum, e) => sum + (e.amount || 0), 0);
          const uniqueCategories = [...new Set(expenseList.map(e => e.category))];
          setCategories(uniqueCategories);
          setStats({
            total: expenseList.length,
            this_month: expenseList.filter(e => {
              const d = new Date(e.date || e.expense_date);
              const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length,
            categories: uniqueCategories.length,
            total_amount: totalAmount,
          });
        }
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load expenses'));
    }
    setLoading(false);
  };

  const filteredExpenses = expenses.filter(exp =>
    (exp.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (exp.notes || exp.description || '')?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleView = (expense) => navigate(`/business/${bid}/expenses/${expense.id}`);
  const handleEdit = (expense) => navigate(`/business/${bid}/expenses/${expense.id}/edit`);

  const handleDownloadPdf = async (expense) => {
    try {
      const res = await expensesAPI.pdf(expense.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_${expense.expense_number || expense.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to download PDF'));
    }
  };

  const handleDelete = (expense) => {
    setConfirmModal({
      title: 'Delete Expense',
      description: `Are you sure you want to delete this ${expense.category || ''} expense of Rs.${(expense.amount || 0).toLocaleString('en-IN')}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await expensesAPI.delete(expense.id);
          toast.success('Expense deleted');
          fetchExpenses();
        } catch (e) {
          toast.error(getApiErrorMessage(e, 'Failed to delete expense'));
        }
      },
    });
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageSlideIn">
      {/* PREMIUM PAGE HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[18px] font-semibold text-slate-800">Expenses</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Track and manage business expenses</p>
            </div>
            <button
              onClick={() => navigate(`/business/${bid}/expenses/new`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Record Expense
            </button>
          </div>
        </div>
      </div>

      {/* Stats & Filters Section */}
      <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-4">
        <div className="max-w-[1600px] mx-auto">
          {/* Stats Pills */}
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center">
                <Receipt className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total</p>
                <p className="text-[16px] font-bold text-slate-700">{stats.total}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">This Month</p>
                <p className="text-[16px] font-bold text-blue-600">{stats.this_month}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Tag className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Categories</p>
                <p className="text-[16px] font-bold text-amber-600">{stats.categories}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Spent</p>
                <p className="text-[16px] font-bold text-teal-600">{formatCompactCurrency(stats.total_amount)}</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by category, vendor, notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-md pl-11 pr-4 py-2.5 bg-white rounded-xl text-[14px] border-2 border-slate-200 focus:border-teal-500 focus:ring-0 focus:outline-none transition-all duration-200 placeholder:text-slate-400 shadow-sm"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-white rounded-lg text-[13px] text-slate-700 border-0 shadow-sm focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col">
          <div className="tl-table-container flex-1 flex flex-col overflow-hidden">

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading expenses...</p>
                </div>
              </div>
            ) : filteredExpenses.length === 0 ? (
              <div className="tl-empty-state flex-1 flex flex-col items-center justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <Receipt className="w-7 h-7 text-slate-400" />
                </div>
                <p className="tl-empty-title">
                  {searchQuery ? 'No expenses found' : 'No expenses recorded yet'}
                </p>
                <p className="tl-empty-description mt-1 mb-4">
                  {searchQuery ? 'Try adjusting your search' : 'Record your first expense to get started'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate(`/business/${bid}/expenses/new`)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
                  >
                    Record Expense
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>Date</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '16%' }}>Category</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '16%' }}>Vendor</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>Paid By</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '12%' }}>Mode</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100 tl-th-right" style={{ width: '14%' }}>Amount</th>
                        <th className="tl-th bg-teal-50/60 text-teal-700 border-b border-teal-100" style={{ width: '8%' }}></th>
                      </tr>
                    </thead>
                  </table>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody className="animate-stagger">
                      {filteredExpenses.map((expense, index) => {
                        const modeConfig = PAYMENT_MODE_CONFIG[expense.payment_mode] || PAYMENT_MODE_CONFIG.CASH;
                        const ModeIcon = modeConfig.icon;
                        const categoryColor = CATEGORY_COLORS[expense.category] || CATEGORY_COLORS['Other'];
                        return (
                          <tr
                            key={expense.id}
                            className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                            onClick={() => handleView(expense)}
                            style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                          >
                            <td className="tl-td" style={{ width: '12%' }}>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                  <Receipt className="w-4 h-4 text-teal-600" />
                                </div>
                                <span className="tl-cell-regular">
                                  {(expense.date || expense.expense_date) ? new Date(expense.date || expense.expense_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '--'}
                                </span>
                              </div>
                            </td>
                            <td className="tl-td" style={{ width: '16%' }}>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${categoryColor}`}>
                                {expense.category}
                              </span>
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '16%' }}>
                              {expense.vendor_name || '--'}
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '12%' }}>
                              {expense.paid_by_name || '--'}
                            </td>
                            <td className="tl-td" style={{ width: '12%' }}>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${modeConfig.bg} ${modeConfig.text}`}>
                                <ModeIcon className="w-3 h-3" /> {modeConfig.label}
                              </span>
                            </td>
                            <td className="tl-td tl-td-right" style={{ width: '14%' }}>
                              <span className="text-[14px] font-bold text-teal-600">₹{(expense.amount || 0).toLocaleString('en-IN')}</span>
                            </td>
                            <td className="tl-td" style={{ width: '8%' }} onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <DropdownMenuItem onClick={() => handleView(expense)}>
                                    <Eye className="w-4 h-4 mr-2" /> View
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEdit(expense)}>
                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDownloadPdf(expense)}>
                                    <Download className="w-4 h-4 mr-2" /> Download PDF
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(expense)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default ExpensesPage;
